import { takeLatest, put, call, all } from 'redux-saga/effects';
import axios from 'axios';
import toastr from 'toastr';
import types from './actionTypes';
import * as actions from './actions';

// API calls for operations that need to interact with Stripe
const api = {
  validateDiscountCode: (code, plan) =>
    axios.post(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes/validate`,
      { code, plan },
    ),

  applyDiscountCode: (discountCodeId, userId) =>
    axios.post(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes/apply`,
      { discountCodeId, userId },
    ),

  createDiscountCode: (discountCodeData) =>
    axios.post(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes/create`,
      discountCodeData,
    ),

  updateDiscountCode: (id, discountCodeData) =>
    axios.put(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes/${id}`,
      discountCodeData,
    ),

  deleteDiscountCode: (id) =>
    axios.delete(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes/${id}`,
    ),

  getDiscountCodes: () =>
    axios.get(
      `https://us-central1-${process.env.REACT_APP_PROJECTID}.cloudfunctions.net/api/discount-codes`,
    ),
};

// Sagas for complex operations (validation and application) - Use Cloud Functions
function* validateDiscountCodeSaga({ code, plan }) {
  try {
    const response = yield call(api.validateDiscountCode, code, plan);
    yield put(actions.validateDiscountCodeSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      'Errore durante la validazione del codice sconto';
    yield put(actions.validateDiscountCodeFailure(errorMessage));
  }
}

function* applyDiscountCodeSaga({ discountCodeId, userId }) {
  try {
    yield call(api.applyDiscountCode, discountCodeId, userId);
    yield put(
      actions.applyDiscountCodeSuccess('Codice sconto applicato con successo'),
    );
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      "Errore durante l'applicazione del codice sconto";
    yield put(actions.applyDiscountCodeFailure(errorMessage));
  }
}

// Sagas for CRUD operations - Use Cloud Functions for Stripe integration
function* fetchDiscountCodesSaga() {
  try {
    const response = yield call(api.getDiscountCodes);
    const discountCodes = response.data.map((discountCode) => ({
      ...discountCode,
      expirationDate: discountCode.expirationDate
        ? new Date(discountCode.expirationDate)
        : null,
      createdAt: discountCode.createdAt
        ? new Date(discountCode.createdAt)
        : null,
      updatedAt: discountCode.updatedAt
        ? new Date(discountCode.updatedAt)
        : null,
    }));
    yield put(actions.fetchDiscountCodesSuccess(discountCodes));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      'Errore durante il recupero dei codici sconto';
    yield put(actions.fetchDiscountCodesFailure(errorMessage));
  }
}

function* createDiscountCodeSaga({ discountCodeData }) {
  try {
    const response = yield call(api.createDiscountCode, discountCodeData);
    const createdDiscountCode = {
      ...response.data,
      expirationDate: response.data.expirationDate
        ? new Date(response.data.expirationDate)
        : null,
      createdAt: response.data.createdAt
        ? new Date(response.data.createdAt)
        : new Date(),
      updatedAt: response.data.updatedAt
        ? new Date(response.data.updatedAt)
        : new Date(),
    };

    yield put(
      actions.createDiscountCodeSuccess(
        createdDiscountCode,
        'Codice sconto creato con successo',
      ),
    );
    toastr.success('Codice sconto creato con successo!', '');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      'Errore durante la creazione del codice sconto';
    yield put(actions.createDiscountCodeFailure(errorMessage));
    toastr.error(errorMessage, 'Errore');
  }
}

function* updateDiscountCodeSaga({ id, discountCodeData }) {
  try {
    yield call(api.updateDiscountCode, id, discountCodeData);
    const updatedDiscountCode = {
      id,
      ...discountCodeData,
      expirationDate: discountCodeData.expirationDate
        ? new Date(discountCodeData.expirationDate)
        : null,
      updatedAt: new Date(),
    };

    yield put(
      actions.updateDiscountCodeSuccess(
        updatedDiscountCode,
        'Codice sconto aggiornato con successo',
      ),
    );
    toastr.success('Codice sconto aggiornato con successo!', '');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      "Errore durante l'aggiornamento del codice sconto";
    yield put(actions.updateDiscountCodeFailure(errorMessage));
    toastr.error(errorMessage, 'Errore');
  }
}

function* deleteDiscountCodeSaga({ id }) {
  try {
    yield call(api.deleteDiscountCode, id);
    yield put(
      actions.deleteDiscountCodeSuccess(
        id,
        'Codice sconto eliminato con successo',
      ),
    );
    toastr.success('Codice sconto eliminato con successo!', '');
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.message ||
      "Errore durante l'eliminazione del codice sconto";
    yield put(actions.deleteDiscountCodeFailure(errorMessage));
    toastr.error(errorMessage, 'Errore');
  }
}

function* discountCodesSaga() {
  yield all([
    yield takeLatest(
      types.VALIDATE_DISCOUNT_CODE.REQUEST,
      validateDiscountCodeSaga,
    ),
    yield takeLatest(types.APPLY_DISCOUNT_CODE.REQUEST, applyDiscountCodeSaga),
    yield takeLatest(
      types.FETCH_DISCOUNT_CODES.REQUEST,
      fetchDiscountCodesSaga,
    ),
    yield takeLatest(
      types.CREATE_DISCOUNT_CODE.REQUEST,
      createDiscountCodeSaga,
    ),
    yield takeLatest(
      types.UPDATE_DISCOUNT_CODE.REQUEST,
      updateDiscountCodeSaga,
    ),
    yield takeLatest(
      types.DELETE_DISCOUNT_CODE.REQUEST,
      deleteDiscountCodeSaga,
    ),
  ]);
}

export default discountCodesSaga;
